import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../account/auth.service';
import { LimitedMember } from '../../shared/models/limited-member';
import { ProfileService } from '../../shared/profile/profile/profile.service';
import { AppService } from '../../shared/services/app.service';
import { GlobalService } from '../../shared/services/global.service';
import { AddCheckinService } from '../../usher/check-in/add-checkin/add-checkin.service';
import { SelfCheckinFlowService, VerifiedCheckinPhone } from './self-checkin-flow.service';
import { SelfCheckinContextService } from './self-checkin-context.service';

export type SelfCheckinKind = 'event' | 'service' | 'group' | 'venue';
export function ownerSelection(members: LimitedMember[], ownerId?: number): number | undefined {
  if (ownerId) return members.some(member => member.id === ownerId) ? ownerId : undefined;
  const primary = members.filter(member => member.checked === true);
  return primary.length === 1 ? primary[0].id : undefined;
}
export function validHouseholdId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value || '');
}

@Component({selector:'app-self-checkin', templateUrl:'./self-checkin.component.html',
  styleUrls:['./self-checkin.component.scss'], standalone:false})
export class SelfCheckinComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() kind: SelfCheckinKind = 'event';
  @Input() targetId: number;
  members: LimitedMember[] = [];
  confirmedMembers: LimitedMember[] = [];
  userId: string = null;
  ownerId?: number;
  loading = false;
  submitting = false;
  loadError = '';
  checkinError = '';
  invalidLink = false;
  inviteSignup = false;
  showHelp = false;
  success = false;
  phoneStep = 0;
  contextName = '';
  private verifiedPhone?: VerifiedCheckinPhone;
  private generation = 0;
  private destroyed = false;
  private headingTimer: number;
  constructor(private profile: ProfileService, private checkins: AddCheckinService,
    public globalService: GlobalService, private router: Router, private app: AppService,
    private auth: AuthService, private flow: SelfCheckinFlowService,
    private context: SelfCheckinContextService) {}

  get tenant(): string { return localStorage.getItem('tenant') || this.globalService.currentTenant || ''; }
  get selectedCount(): number { return this.members.filter(member => member.checked).length; }
  get contextLabel(): string {
    if (this.contextName) {
      const categories = this.globalService.isRTL
        ? {event:'المناسبة', service:'الخدمة', group:'الخدمة الكنسية', venue:'الموقع'}
        : {event:'Event', service:'Service', group:'Ministry', venue:'Location'};
      return `${categories[this.kind]} · ${this.contextName}`;
    }
    const labels = this.globalService.isRTL
      ? {event:'تسجيل حضور المناسبة', service:'تسجيل حضور الخدمة', group:'تسجيل حضور مجموعة الخدمة', venue:'تسجيل حضور الموقع'}
      : {event:'Event check-in', service:'Service check-in', group:'Ministry check-in', venue:'Location check-in'};
    return labels[this.kind];
  }
  text(english: string, arabic: string): string { return this.globalService.isRTL ? arabic : english; }
  ngOnInit(): void { void this.initialize(); }
  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some(change => !change.firstChange)) void this.initialize();
  }
  ngAfterViewInit(): void {
    this.headingTimer = window.setTimeout(() => {
      if (this.destroyed) return;
      this.globalService.title.push(this.globalService.screenHeader);
      this.globalService.screenHeader = this.globalService.Get('SELF_CHECKIN');
    });
  }
  ngOnDestroy(): void { this.destroyed = true; this.generation++; clearTimeout(this.headingTimer); }

  async initialize(): Promise<void> {
    const generation = ++this.generation;
    this.members = []; this.loading = false; this.submitting = false; this.success = false; this.inviteSignup = false;
    this.loadError = ''; this.checkinError = ''; this.ownerId = undefined;
    this.showHelp = false; this.phoneStep = 0; this.verifiedPhone = undefined; this.confirmedMembers = [];
    this.invalidLink = !Number.isSafeInteger(this.targetId) || this.targetId <= 0;
    this.userId = null;
    this.contextName = '';
    if (this.invalidLink) return;
    void this.loadContextName();
    const saved = localStorage.getItem('CSBarcodeID');
    if (!validHouseholdId(saved)) { if (saved) localStorage.removeItem('CSBarcodeID'); return; }
    this.userId = saved;
    const session = this.auth.user.value;
    if (session?.id === saved) this.ownerId = session.memberId;
    await this.loadHousehold(generation);
  }

  private async loadContextName(): Promise<void> {
    const tenant = this.tenant, kind = this.kind, target = this.targetId;
    const name = await this.context.name(tenant, kind, target);
    if (!this.destroyed && this.tenant === tenant && this.kind === kind && this.targetId === target)
      this.contextName = name;
  }

  async loadHousehold(generation = this.generation): Promise<void> {
    if (!this.userId || this.loading) return;
    this.loading = true; this.loadError = '';
    try {
      const members = await this.profile.getLimitedMembers(this.userId);
      if (this.destroyed || generation !== this.generation) return;
      if (!members?.length) { this.resetHousehold(); return; }
      this.ownerId = ownerSelection(members, this.ownerId);
      this.members = members.map(member => ({...member, checked: member.id === this.ownerId}));
    } catch (error) {
      if (this.destroyed || generation !== this.generation) return;
      if ([400,401,403,404].includes(error?.status)) this.resetHousehold();
      else this.loadError = this.text('Check your connection and try again. Your household could not be loaded.', 'تحقق من الاتصال وحاول مرة أخرى. تعذر تحميل الأسرة.');
    } finally { if (generation === this.generation) this.loading = false; }
  }

  resetHousehold(): void {
    if (this.submitting) return;
    this.generation++; this.loading = false; this.members = []; this.userId = null;
    this.ownerId = undefined; this.inviteSignup = false; this.success = false; this.phoneStep = 0;
    this.showHelp = false; this.confirmedMembers = [];
    this.verifiedPhone = undefined; this.loadError = ''; this.checkinError = '';
    this.flow.clear(); localStorage.removeItem('CSBarcodeID');
  }
  async phoneVerified(phone: VerifiedCheckinPhone): Promise<void> {
    if (this.destroyed || this.invalidLink) return;
    this.verifiedPhone = phone; this.loadError = '';
    if (!phone.userId) {
      if (!phone.verificationToken) {
        this.loadError = this.text('Please verify your phone again to continue.', 'يرجى تأكيد رقم الهاتف مرة أخرى للمتابعة.'); return;
      }
      this.inviteSignup = true; return;
    }
    if (!validHouseholdId(phone.userId)) {
      this.loadError = this.text('We could not identify your household. Please verify your phone again.', 'تعذر تحديد الأسرة. يرجى تأكيد الهاتف مرة أخرى.'); return;
    }
    this.userId = phone.userId; this.ownerId = phone.memberId;
    if (localStorage.getItem('CSBarcodeID') !== phone.userId) {
      localStorage.setItem('CSBarcodeID', phone.userId);
      this.app.sendToNative(phone.userId, this.tenant);
    }
    await this.loadHousehold();
  }
  signup(): void {
    if (!this.verifiedPhone || !this.flow.begin(this.tenant, this.router.url, this.verifiedPhone)) {
      this.resetHousehold(); return;
    }
    void this.router.navigate(['/' + this.tenant + '/signup']);
  }
  toggle(member: LimitedMember): void { if (!this.submitting) { member.checked = !member.checked; this.checkinError = ''; } }
  selectEveryone(): void {
    if (this.submitting) return;
    const selected = this.selectedCount !== this.members.length;
    this.members.forEach(member => member.checked = selected); this.checkinError = '';
  }
  trackMember(index: number, member: LimitedMember): number { return member.id; }
  async submitCheckin(): Promise<void> {
    if (this.submitting || this.success || this.invalidLink || !this.userId || !this.selectedCount) return;
    const selected = this.members.filter(member => member.checked);
    const generation = this.generation;
    this.submitting = true; this.checkinError = '';
    try {
      const methods = {event:'AddCheckIn', service:'AddCheckInByService', group:'AddCheckInByGroup', venue:'AddCheckInByVenue'};
      const result = await this.checkins[methods[this.kind]](this.targetId, selected.map(member => member.id), this.userId, 'user-' + this.userId);
      if (this.destroyed || generation !== this.generation) return;
      // A missing/malformed response is never treated as attendance confirmation.
      if (!result || typeof result.message !== 'string') throw new Error('Missing check-in response');
      if (result.message) { this.checkinError = result.message; return; }
      this.confirmedMembers = selected.map(member => ({...member})); this.success = true;
    } catch {
      if (!this.destroyed && generation === this.generation)
        this.checkinError = this.text('Check-in did not complete. Your selection is still here. Please try again.', 'لم يكتمل تسجيل الحضور. تم الاحتفاظ باختياراتك. يرجى المحاولة مرة أخرى.');
    } finally { if (generation === this.generation) this.submitting = false; }
  }
  done(): void { this.flow.clear(); void this.router.navigate(['/' + this.tenant + '/home']); }
}
